import { z } from "zod";

import { env } from "@/env";
import { fetchApi, fetchSpreedlyApi } from "@/lib/api-helpers";
import { billingAddressSchema } from "./addresses.api";

export enum PaymentBehavior {
  CREDIT_CARD = "credit_card",
  MODAL_BASED = "modal_based",
  REDIRECT_BASED = "redirect_based",
  DIRECT_CHECKOUT = "direct_checkout",
  CUSTOM_COMPONENT = "custom_component",
}

export const SPECIAL_PAYMENT_METHODS = {
  credit_card: PaymentBehavior.CREDIT_CARD,
  affirm: PaymentBehavior.MODAL_BASED,
  klarna: PaymentBehavior.MODAL_BASED,
  paypal: PaymentBehavior.CUSTOM_COMPONENT,
  droplet: PaymentBehavior.DIRECT_CHECKOUT,
  // Everything else will be treated as redirect-based by default
} as const;

export const spreedlyResponseSchema = z.object({
  transaction: z.object({
    token: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    succeeded: z.boolean(),
    transaction_type: z.string(),
    retained: z.boolean(),
    state: z.string(),
    message_key: z.string(),
    message: z.string(),
    payment_method: z.object({
      token: z.string(),
      created_at: z.string(),
      updated_at: z.string(),
      email: z.string().nullable(),
      data: z.unknown().nullable(),
      storage_state: z.string(),
      test: z.boolean(),
      metadata: z.unknown().nullable(),
      callback_url: z.string().nullable(),
      last_four_digits: z.string(),
      first_six_digits: z.string(),
      card_type: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      month: z.number(),
      year: z.number(),
      address1: z.string().nullable(),
      address2: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
      country: z.string().nullable(),
      phone_number: z.string().nullable(),
      company: z.string().nullable(),
      full_name: z.string(),
      eligible_for_card_updater: z.boolean(),
      shipping_address1: z.string().nullable(),
      shipping_address2: z.string().nullable(),
      shipping_city: z.string().nullable(),
      shipping_state: z.string().nullable(),
      shipping_zip: z.string().nullable(),
      shipping_country: z.string().nullable(),
      shipping_phone_number: z.string().nullable(),
      issuer_identification_number: z.string(),
      click_to_pay: z.boolean(),
      managed: z.boolean(),
      bin_metadata: z.object({
        message: z.string(),
      }),
      payment_method_type: z.string(),
      errors: z.array(z.unknown()),
      fingerprint: z.string(),
      verification_value: z.string(),
      number: z.string(),
    }),
  }),
});

export async function tokenizeCreditCard(body: {
  credit_card: {
    number: string;
    verification_value: string;
    month: string;
    year: string;
    first_name: string;
    last_name: string;
  };
}) {
  return fetchSpreedlyApi({
    endpoint: `payment_methods.json?environment_key=${env.NEXT_PUBLIC_SPREEDLY_API_KEY}`,
    input: body,
    outputSchema: spreedlyResponseSchema,
  });
}

export const creditCardInputSchema = z.object({
  billing_postal_code: z.string(),
  payment_method: z.object({
    token: z.string(),
    last_four: z.string(),
    card_type: z.string(),
    exp_month: z.string(),
    exp_year: z.string(),
  }),
});

export const affirmPaymentMethodSchema = z.object({
  payment_method: z.object({
    token: z.string(),
  }),
});

export async function addCreditCard({
  cartToken,
  body,
}: {
  cartToken: string;
  body: z.infer<typeof creditCardInputSchema>;
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/credit_card`,
    method: "POST",
    input: body,
    inputSchema: creditCardInputSchema,
    outputSchema: z.object({
      card: z.object({
        spreedly_token: z.string(),
      }),
      status: z.string(),
    }),
  });
}

export async function addAffirmPaymentToken({
  cartToken,
  paymentMethod,
}: {
  cartToken: string;
  paymentMethod: {
    token: string;
  };
}) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/affirm`,
    method: "POST",
    input: { payment_method: paymentMethod },
    inputSchema: affirmPaymentMethodSchema,
    outputSchema: z.object({}),
  });
}

export const vgsCmpPaymentMethodSchema = z.object({
  type: z.literal("credit_card"),
  card_number: z.string().min(1, "Card number is required"),
  cvc: z.string().min(3, "CVV is required"),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  expiration_date: z.string().optional(),
  billing_zip: z.string().optional(),
  token_provider: z.literal("vgs"),
  vault_card_id: z.string().optional(),
  card_fingerprint: z.string().optional(),
});

export const paymentMethodSchema = z
  .object({
    type: z.string().min(1, "Payment type is required"),
    card_type: z.string().optional(),
    card_number: z.string().optional(),
    expiration_date: z.string().optional(),
    cvc: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    billing_address: billingAddressSchema.partial().optional(),
    token_provider: z.string().optional(),
    vault_card_id: z.string().optional(), // CMP specific
    card_fingerprint: z.string().optional(), // CMP specific
    token: z.string().optional(), // Legacy alias approach
    cvv_token: z.string().optional(), // Legacy alias approach
    use_shipping_address_for_billing: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === "credit_card") {
      const isVgs = data.token_provider === "vgs";

      if (!data.use_shipping_address_for_billing && data.billing_address) {
        const result = billingAddressSchema.safeParse(data.billing_address);
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ["billing_address", ...issue.path],
            });
          });
        }
      }
      if (!data.card_number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["card_number"],
          message: "Card number is required",
        });
      }

      if (!data.cvc || data.cvc.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["cvc"],
          message: "CVC must be at least 3 digits",
        });
      }

      if (!isVgs) {
        // Standard validation for non-VGS payments
        if (!data.expiration_date || data.expiration_date.length < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expiration_date"],
            message: "Expiration date is required",
          });
        }

        if (!data.first_name || data.first_name.length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["first_name"],
            message: "First name is required",
          });
        }

        if (!data.last_name || data.last_name.length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["last_name"],
            message: "Last name is required",
          });
        }
      } else {
        // VGS CMP validation - less strict since CMP handles tokenization
        if (!data.expiration_date || data.expiration_date.length < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expiration_date"],
            message: "Expiration date is required",
          });
        }
      }
    }
  });

export const returnedPaymentMethodSchema = z
  .object({
    id: z.number(),
    type: z.string(),
    payment_method_type: z.string().optional(),
    last_four: z.string().optional(),
    // Accept both string and number, then transform to string
    exp_month: z
      .union([z.string(), z.number()])
      .transform((val) => String(val))
      .optional(),
    exp_year: z
      .union([z.string(), z.number()])
      .transform((val) => String(val))
      .optional(),
    logo_url: z.string().optional(),
    token_provider: z.string().optional(),
    vault_card_id: z.string().optional(),
    card_fingerprint: z.string().optional(),
    token: z.string().optional(),
    cvv_token: z.string().optional(),
    // Affirm specific
    affirm_id: z.string().optional(),
    status: z.string().optional(),
    checkout_id: z.string().optional(),
    created: z.string().optional(),
    auth_expiration: z.string().optional(),
    currency: z.string().optional(),
    amount: z.number().optional(),
    customer_details: z.record(z.string()).nullable().optional(),
  })
  .passthrough();

export type PaymentMethod = z.infer<typeof returnedPaymentMethodSchema>;

export const cardSchema = z.object({
  user_id: z.number().nullable(),
  spreedly_token: z.string(),
  full_name: z.string(),
  month: z.number(),
  year: z.number(),
  number: z.string(),
  last_four_digits: z.string(),
  first_six_digits: z.string(),
  card_type: z.string(),
  address_id: z.number(),
  fingerprint: z.string(),
  default: z.boolean(),
  active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  fluid: z.boolean(),
  test: z.boolean().nullable(),
  email: z.string(),
  customer_id: z.number().nullable(),
  fluid_pay_account_id: z.number().nullable(),
});

export const paymentResponseSchema = z.object({
  card: cardSchema,
  status: z.string(),
});

const customerMetaSchema = z.object({
  request_id: z.string(),
  timestamp: z.string(),
  pagination: z
    .object({
      current_page: z.number(),
      total_pages: z.number(),
      total_count: z.number(),
      per_page: z.number(),
    })
    .optional(),
});

const customerPaymentDetailsSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  default: z.boolean(),
  details: z.object({
    card_type: z.string().optional(),
    last_four: z.string().optional(),
    exp_month: z.union([z.string(), z.number()]).optional(),
    exp_year: z.union([z.string(), z.number()]).optional(),
    logo_url: z.string().optional(),
    card_brand: z.string().optional(),
  }),
  payload: z.unknown().optional(),
  payment_type: z.string(),
  source: z.string(),
  updated_at: z.string().optional(),
});

export type CustomerPaymentMethod = z.infer<
  typeof customerPaymentDetailsSchema
>;

const createCustomerPaymentMethodSchema = z.object({
  payment_method: z.object({
    id: z.number(),
    created_at: z.string(),
    default: z.boolean(),
    details: z.object({
      card_type: z.string(),
      last_four: z.string(),
      exp_month: z.union([z.string(), z.number()]),
      exp_year: z.union([z.string(), z.number()]),
      logo_url: z.string(),
      card_brand: z.string(),
    }),
    payment_type: z.string(),
    source: z.string(),
    updated_at: z.string(),
  }),
});

export type CreateCustomerPaymentMethodResponse = z.infer<
  typeof createCustomerPaymentMethodSchema
>;

const customerPaymentMethodsSchema = z.object({
  fluid_pay_account: z.object({
    id: z.number(),
    default_payment_method_id: z.number().nullable(),
  }),
  payment_methods: z.array(customerPaymentDetailsSchema),
  meta: customerMetaSchema,
});

export type CustomerPaymentMethodsResponse = z.infer<
  typeof customerPaymentMethodsSchema
>;

export const creditCardFormDataSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  card_number: z.string().min(1, "Card number is required"),
  card_type: z.string().optional(),
  expiration_date: z.string().min(5, "Expiration date must be in MM/YY format"),
  cvc: z.string().min(3, "CVC must be at least 3 digits"),
  default: z.boolean(),
  billing_address: z.object({
    address1: z.string().min(1, "Address line 1 is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State is required"),
    zip: z.string().min(1, "Zip code is required"),
    country: z.string().min(1, "Country is required"),
  }),
});

export type CreditCardFormData = z.infer<typeof creditCardFormDataSchema>;

export async function fetchCustomerPaymentMethods(jwt: string) {
  const query = new URLSearchParams({
    jwt,
    page: "1",
    per_page: "100",
  });
  return fetchApi({
    endpoint: `/fluid_pay/payment_methods?${query}`,
    method: "GET",
    outputSchema: customerPaymentMethodsSchema,
  });
}

export async function updateCartPaymentMethod(
  cartToken: string,
  jwt: string,
  paymentMethodId: string
) {
  const query = new URLSearchParams({
    jwt,
    payment_method_id: paymentMethodId,
  });

  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/credit_card?${query}`,
    method: "POST",
    outputSchema: z.object({}),
  });
}

export async function addCreditCardToCustomer(
  jwt: string,
  paymentMethodData: {
    type: string;
    country_code: string;
    default_payment_method: boolean;
    // Payment method data nested under payment_method
    payment_method: {
      token: string;
      token_provider?: string;
      vault_card_id?: string;
      card_fingerprint?: string;
      last_four?: string;
      card_type?: string;
      exp_month?: string;
      exp_year?: string;
      card_holder?: string;
      billing_zip?: string; // Keep for backward compatibility
    };
    billing_address?: {
      name?: string;
      address1: string;
      address2?: string;
      city: string;
      state: string;
      zip: string;
      country_code: string;
    };
    browser_info?: Record<string, unknown>;
  }
) {
  const query = new URLSearchParams({
    jwt,
  });

  return fetchApi({
    endpoint: `/fluid_pay/create_payment_method?${query}`,
    method: "POST",
    input: paymentMethodData,
    outputSchema: createCustomerPaymentMethodSchema,
  });
}

export async function deleteCustomerPaymentMethod(
  jwt: string,
  paymentMethodId: string
) {
  return fetchApi({
    endpoint: `/fluid_pay/delete_payment_method/${paymentMethodId}?jwt=${jwt}`,
    method: "DELETE",
    outputSchema: z.any(),
  });
}

export async function createPaypalOrder(cart: {
  id: string;
  quantity: string;
}) {
  return fetchApi({
    endpoint: `/payments/paypal_create_order`,
    method: "POST",
    input: { cart },
    outputSchema: z.any(),
  });
}

const API_VER = "v202506";

const klarnaSessionResponseSchema = z.object({
  success: z.boolean(),
  session_id: z.string(),
  client_token: z.string(),
  meta: z.object({
    request_id: z.string().optional(),
    timestamp: z.string().optional(),
  }),
});

export async function createKlarnaSession({
  cartToken,
  intent = "buy",
}: {
  cartToken: string;
  intent?: "buy" | "tokenize" | "buy_and_tokenize" | "buy_and_default_tokenize";
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/klarna/create_session`,
    method: "POST",
    input: { intent },
    outputSchema: klarnaSessionResponseSchema,
  });
}

export async function updateKlarnaSession({
  cartToken,
  sessionId,
}: {
  cartToken: string;
  sessionId: string;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/klarna/update_session`,
    method: "POST",
    input: { session_id: sessionId },
    outputSchema: z.object({ success: z.boolean(), meta: z.any() }),
  });
}

export const klarnaPaymentMethodSchema = z.object({
  payment_method: z.object({
    token: z.string(),
  }),
});

export async function addKlarnaPaymentToken({
  cartToken,
  authorization_token,
  session_id,
}: {
  cartToken: string;
  authorization_token: string;
  session_id: string;
}) {
  const payload: {
    payment_method: {
      authorization_token: string;
      session_id: string;
    };
  } = {
    payment_method: {
      authorization_token,
      session_id,
    },
  };

  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/klarna`,
    method: "POST",
    input: payload,
    outputSchema: z.object({}),
  });
}

export const fluidPayAccountResponseSchema = z.object({
  fluid_pay_account: z.object({
    id: z.number(),
  }),
  default_payment_method: z.object({
    id: z.number(),
    source: z.string(),
    default: z.boolean(),
    payment_type: z.string(),
    details: z.record(z.unknown()),
    created_at: z.string(),
    updated_at: z.string(),
  }),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

export async function updateDefaultPaymentMethod(
  jwt: string,
  paymentMethodId: string
) {
  return fetchApi({
    endpoint: `/fluid_pay/update_payment_method/${paymentMethodId}?jwt=${jwt}`,
    method: "PATCH",
    outputSchema: fluidPayAccountResponseSchema,
  });
}

export async function addRedirectPaymentMethod({
  cartToken,
  paymentType,
  payment_account_id,
  customer_details,
}: {
  cartToken: string;
  paymentType: string;
  payment_account_id?: number;
  customer_details?: Record<string, string>;
}) {
  const payload: {
    payment_account_id?: number;
    customer_details?: Record<string, string>;
  } = {};
  if (payment_account_id) {
    payload.payment_account_id = payment_account_id;
  }
  if (customer_details && Object.keys(customer_details).length > 0) {
    payload.customer_details = customer_details;
  }

  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/${paymentType}`,
    method: "POST",
    input: payload,
    outputSchema: z.object({}),
  });
}
